import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion as m, useScroll } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { setDark } from "../store/actions";
import { navLinks, socialLinks, site } from "../data/site";

const menuVariants = {
  closed: { clipPath: "inset(0% 0% 100% 0%)" },
  open: { clipPath: "inset(0% 0% 0% 0%)" },
};

const SunIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
    <path d="M9.8 2.2a9 9 0 1 0 12 12A9.6 9.6 0 0 1 9.8 2.2z" />
  </svg>
);

const Navbar = () => {
  const theme = useSelector((state) => state.darkmode);
  const dispatch = useDispatch();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrollY } = useScroll();

  useEffect(() => {
    const unsubscribe = scrollY.on("change", (v) => setScrolled(v > 24));
    return () => unsubscribe();
  }, [scrollY]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", Boolean(theme));
  }, [theme]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    // Lenis listens on window, so native overflow alone will not hold it.
    if (window.__lenis) {
      if (menuOpen) window.__lenis.stop();
      else window.__lenis.start();
    }
    return () => {
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    };
  }, [menuOpen]);

  const toggleTheme = () => dispatch(setDark(!theme));

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ease-standard ${
          scrolled
            ? "border-b border-[var(--color-border)] bg-paper/80 py-3 shadow-sm backdrop-blur-xl dark:bg-night/80"
            : "border-b border-transparent py-6"
        }`}
      >
        <div className="mx-auto flex w-full max-w-content items-center justify-between px-[var(--gutter)]">
          {/* Monogram */}
          <Link to="/" className="group flex items-center gap-3" aria-label="Home">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-azure font-display text-base font-semibold text-paper transition-transform duration-500 ease-emphasis group-hover:rotate-[14deg] dark:bg-electric dark:text-paper">
              {site.initials}
            </span>
            <span className="hidden leading-tight sm:block">
              <span className="block font-display text-[15px] font-semibold tracking-tight">
                {site.shortName}
              </span>
              <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted dark:text-slightwhite/50">
                {site.role}
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-9 lg:flex">
            {navLinks.map(({ name, link }) => (
              <NavLink
                key={link}
                to={link}
                end={link === "/"}
                className={({ isActive }) =>
                  `link-underline font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                    isActive
                      ? "text-azure dark:text-electric"
                      : "text-ink-muted hover:text-ink dark:text-slightwhite/60 dark:hover:text-slightwhite"
                  }`
                }
              >
                {({ isActive }) => (
                  <span data-active={isActive}>{name}</span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              aria-label={theme ? "Switch to light mode" : "Switch to dark mode"}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--color-border)] text-ink-muted transition-all duration-300 hover:border-azure hover:text-azure dark:text-slightwhite/70 dark:hover:border-electric dark:hover:text-electric"
            >
              {theme ? <SunIcon /> : <MoonIcon />}
            </button>

            <Link
              to="/contact"
              className="hidden rounded-full bg-ink px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-paper transition-all duration-300 hover:bg-azure dark:bg-slightwhite dark:text-ink dark:hover:bg-electric md:inline-flex"
            >
              Start a project
            </Link>

            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              className="flex h-10 w-10 flex-col items-center justify-center gap-[5px] rounded-full border border-[var(--color-border)] lg:hidden"
            >
              <span
                className={`block h-px w-4 bg-current transition-transform duration-300 ${
                  menuOpen ? "translate-y-[3px] rotate-45" : ""
                }`}
              />
              <span
                className={`block h-px w-4 bg-current transition-transform duration-300 ${
                  menuOpen ? "-translate-y-[3px] -rotate-45" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile overlay */}
      <AnimatePresence>
        {menuOpen && (
          <m.div
            variants={menuVariants}
            initial="closed"
            animate="open"
            exit="closed"
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-40 flex flex-col justify-between bg-gradient-brand px-[var(--gutter)] pb-12 pt-32 text-paper lg:hidden"
          >
            <nav className="flex flex-col">
              {navLinks.map(({ name, link }, i) => (
                <m.div
                  key={link}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.06, duration: 0.5 }}
                  className="border-b border-white/10"
                >
                  <Link
                    to={link}
                    className="flex items-baseline gap-4 py-4 font-display text-4xl leading-none"
                  >
                    <span className="font-mono text-[10px] tracking-[0.2em] text-electric">
                      0{i + 1}
                    </span>
                    {name}
                  </Link>
                </m.div>
              ))}
            </nav>

            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.18em] text-paper/70"
            >
              {socialLinks.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target={s.url.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="link-underline hover:text-electric"
                >
                  {s.name}
                </a>
              ))}
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
